/**
 * Step3SendMode Component
 *
 * Allows user to choose between two email sending modes:
 * 1. Individual: Personalized emails with first name (sent with delays)
 * 2. Bulk BCC: All recipients in BCC, single email to user's address
 *
 * Also supports a Test Mode that sends to custom email addresses instead of actual recipients.
 *
 * Mobile UI: Compact single-line elements, dropdown for mode selection,
 * toggle button for override, and modal overlay for message editing.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  User,
  Users,
  ChevronLeft,
  ChevronRight,
  Clock,
  Mail,
  Check,
  FlaskConical,
  X,
  Plus,
  MessageSquarePlus,
  Eye,
  Download,
  FileText,
  Loader2,
  ChevronDown,
  Edit3,
  CheckCircle2,
} from 'lucide-react';
import { useIsMobile } from '@shared/hooks/useIsMobile';

// localStorage key for default BCC email
const STORAGE_KEY_BCC_TO_EMAIL = 'hmc_email_default_bcc_to_email';

interface Step3SendModeProps {
  /** Number of selected recipients */
  recipientCount: number;
  /** Current send mode */
  sendMode: 'individual' | 'bulk-bcc';
  /** Callback when mode changes */
  onSendModeChange: (mode: 'individual' | 'bulk-bcc') => void;
  /** BCC "To" email for bulk mode */
  bccToEmail: string;
  /** Callback when BCC email changes */
  onBccToEmailChange: (email: string) => void;
  /** Test mode enabled */
  testModeEnabled: boolean;
  /** Callback when test mode changes */
  onTestModeChange: (enabled: boolean) => void;
  /** Test email addresses (up to 4) */
  testEmails: string[];
  /** Callback when test emails change */
  onTestEmailsChange: (emails: string[]) => void;
  /** Personal message override content */
  personalMessageOverride: string;
  /** Callback when personal message override changes */
  onPersonalMessageOverrideChange: (message: string) => void;
  /** Whether personal message override is enabled */
  usePersonalMessageOverride: boolean;
  /** Callback when personal message override toggle changes */
  onUsePersonalMessageOverrideChange: (enabled: boolean) => void;
  /** Export handler for Outlook Draft (.eml) download */
  onDownloadOutlookDraft: () => Promise<void>;
  /** Export handler for Word Mail Merge Pack (.zip) download */
  onDownloadMailMergePack: () => Promise<void>;
  /** Loading state during export */
  isExporting: boolean;
  /** Whether this invite has a name (saved invite) - required for Mark as Completed */
  hasInviteName?: boolean;
  /** Whether this invite is already completed */
  isCompleted?: boolean;
  /** Callback when Mark as Completed is clicked */
  onMarkAsCompleted?: () => void;
  /** Navigation */
  onBack: () => void;
  onNext: () => void;
}

// Load default BCC email from localStorage
const loadDefaultBccEmail = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEY_BCC_TO_EMAIL) || '';
  } catch {
    return '';
  }
};

// Save default BCC email to localStorage
const saveDefaultBccEmail = (email: string): void => {
  try {
    if (email.trim()) {
      localStorage.setItem(STORAGE_KEY_BCC_TO_EMAIL, email);
    } else {
      localStorage.removeItem(STORAGE_KEY_BCC_TO_EMAIL);
    }
  } catch {
    // Ignore storage errors
  }
};

export const Step3SendMode: React.FC<Step3SendModeProps> = ({
  recipientCount,
  sendMode,
  onSendModeChange,
  bccToEmail,
  onBccToEmailChange,
  testModeEnabled,
  onTestModeChange,
  testEmails,
  onTestEmailsChange,
  personalMessageOverride,
  onPersonalMessageOverrideChange,
  usePersonalMessageOverride,
  onUsePersonalMessageOverrideChange,
  onDownloadOutlookDraft,
  onDownloadMailMergePack,
  isExporting,
  hasInviteName = false,
  isCompleted = false,
  onMarkAsCompleted,
  onBack,
  onNext,
}) => {
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  // Mobile message edit modal state
  const [showMessageEditModal, setShowMessageEditModal] = useState(false);
  const [tempMessageOverride, setTempMessageOverride] = useState('');

  // Load default BCC email on mount if not already set
  useEffect(() => {
    if (!bccToEmail) {
      const defaultEmail = loadDefaultBccEmail();
      if (defaultEmail) {
        onBccToEmailChange(defaultEmail);
      }
    }
  }, [bccToEmail, onBccToEmailChange]);

  // Calculate estimated send time for individual mode
  const estimatedMinutes = Math.ceil((recipientCount * 0.5) / 60); // 500ms per email
  const estimatedTimeText =
    estimatedMinutes < 1
      ? 'less than a minute'
      : estimatedMinutes === 1
      ? '~1 minute'
      : `~${estimatedMinutes} minutes`;

  // Validate email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Get valid test emails
  const validTestEmails = testEmails.filter((email) => isValidEmail(email));

  // Handle adding a test email
  const handleAddTestEmail = () => {
    if (testEmails.length < 4) {
      onTestEmailsChange([...testEmails, '']);
    }
  };

  // Handle removing a test email
  const handleRemoveTestEmail = (index: number) => {
    const newEmails = testEmails.filter((_, i) => i !== index);
    onTestEmailsChange(newEmails.length > 0 ? newEmails : ['']);
  };

  // Handle updating a test email
  const handleUpdateTestEmail = (index: number, value: string) => {
    const newEmails = [...testEmails];
    newEmails[index] = value;
    onTestEmailsChange(newEmails);
  };

  // Handle inserting {{firstName}} at cursor position
  const handleInsertFirstName = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = personalMessageOverride;
    const token = '{{firstName}}';

    const newText = text.substring(0, start) + token + text.substring(end);
    onPersonalMessageOverrideChange(newText);

    // Restore focus and set cursor position after the inserted token
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  // Generate preview with sample firstName
  const getPersonalMessagePreview = () => {
    return personalMessageOverride.replace(/\{\{firstName\}\}/g, 'Chris');
  };

  // Determine if can proceed
  const canProceed = testModeEnabled
    ? validTestEmails.length > 0
    : sendMode === 'individual' || (sendMode === 'bulk-bcc' && isValidEmail(bccToEmail));

  // Handle proceeding to next step
  const handleNext = () => {
    // Save default if checkbox is checked
    if (saveAsDefault && bccToEmail.trim()) {
      saveDefaultBccEmail(bccToEmail.trim());
    }
    onNext();
  };

  // Mobile: Open message edit modal
  const handleOpenMessageEdit = () => {
    setTempMessageOverride(personalMessageOverride);
    setShowMessageEditModal(true);
  };

  // Mobile: Apply changes from message edit modal
  const handleApplyMessageChanges = () => {
    onPersonalMessageOverrideChange(tempMessageOverride);
    setShowMessageEditModal(false);
  };

  // Mobile: Cancel message edit modal
  const handleCancelMessageEdit = () => {
    setShowMessageEditModal(false);
    setTempMessageOverride('');
  };

  // Mobile: Insert firstName token in temp message
  const handleInsertFirstNameInModal = () => {
    setTempMessageOverride((prev) => prev + '{{firstName}}');
  };

  // Mobile compact UI
  if (isMobile) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-3 py-2 border-b border-dark-border/30">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-white font-medium">Send Mode</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
              {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Compact content */}
        <div className="flex-1 px-3 py-2 overflow-y-auto space-y-2">
          {/* Send Mode Dropdown - Single Line */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-muted whitespace-nowrap w-20">Mode:</span>
            <div className="relative flex-1">
              <select
                value={sendMode}
                onChange={(e) => onSendModeChange(e.target.value as 'individual' | 'bulk-bcc')}
                className="w-full appearance-none bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl px-3 py-2 pr-8 text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="individual">Individual (Personalized)</option>
                <option value="bulk-bcc">Bulk BCC (Single email)</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted pointer-events-none" />
            </div>
          </div>

          {/* BCC Email Input - Single Line (shown when bulk-bcc) */}
          {sendMode === 'bulk-bcc' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-dark-muted whitespace-nowrap w-20">Your email:</span>
              <input
                type="email"
                value={bccToEmail}
                onChange={(e) => onBccToEmailChange(e.target.value)}
                placeholder="you@email.com"
                className={`flex-1 px-3 py-2 bg-dark-surface border rounded-lg text-sm text-white placeholder:text-dark-muted focus:outline-none ${
                  bccToEmail && !isValidEmail(bccToEmail)
                    ? 'border-red-500'
                    : 'border-dark-border/30 focus:border-brand-500'
                }`}
              />
            </div>
          )}

          {/* Time estimate for individual mode - Single Line */}
          {sendMode === 'individual' && (
            <div className="flex items-center gap-2 text-xs text-dark-muted">
              <Clock className="w-3.5 h-3.5 text-yellow-400" />
              <span>Est. time: {estimatedTimeText}</span>
            </div>
          )}

          {/* Test Mode Toggle - Single Line */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-muted whitespace-nowrap w-20">Test Mode:</span>
            <button
              onClick={() => onTestModeChange(!testModeEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 ${
                testModeEnabled ? 'bg-amber-500' : 'bg-dark-border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                  testModeEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            {testModeEnabled && (
              <span className="text-xs text-amber-400">Active</span>
            )}
          </div>

          {/* Test Email Inputs (shown when test mode enabled) */}
          {testModeEnabled && (
            <div className="space-y-1.5 pl-[88px]">
              {testEmails.map((email, index) => (
                <div key={index} className="flex items-center gap-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleUpdateTestEmail(index, e.target.value)}
                    placeholder={`Test email ${index + 1}`}
                    className={`flex-1 px-2 py-1.5 bg-dark-surface border rounded text-xs text-white placeholder:text-dark-muted focus:outline-none ${
                      email && !isValidEmail(email)
                        ? 'border-red-500'
                        : 'border-dark-border/30 focus:border-amber-500'
                    }`}
                  />
                  {testEmails.length > 1 && (
                    <button
                      onClick={() => handleRemoveTestEmail(index)}
                      className="p-1 text-dark-muted hover:text-red-400"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {testEmails.length < 4 && (
                <button
                  onClick={handleAddTestEmail}
                  className="flex items-center gap-1 text-xs text-amber-400"
                >
                  <Plus className="w-3 h-3" />
                  Add email
                </button>
              )}
            </div>
          )}

          {/* Personal Message Override - Single Line with Toggle + Edit */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-muted whitespace-nowrap w-20">Override:</span>
            <button
              onClick={() => onUsePersonalMessageOverrideChange(!usePersonalMessageOverride)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 ${
                usePersonalMessageOverride ? 'bg-amber-500' : 'bg-dark-border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                  usePersonalMessageOverride ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            {usePersonalMessageOverride && sendMode === 'individual' && (
              <button
                onClick={handleOpenMessageEdit}
                className="flex items-center gap-1 px-2 py-1 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded"
              >
                <Edit3 className="w-3 h-3" />
                Edit
              </button>
            )}
          </div>

          {/* Export Options - Single Line Compact Buttons */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-dark-muted whitespace-nowrap w-20">Export:</span>
            <button
              onClick={onDownloadOutlookDraft}
              disabled={isExporting || sendMode !== 'bulk-bcc' || !isValidEmail(bccToEmail)}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-all duration-150 ${
                sendMode === 'bulk-bcc' && isValidEmail(bccToEmail)
                  ? 'bg-blue-500/10 border border-brand-500/30 text-blue-400'
                  : 'bg-dark-surface/30 border border-dark-border/30/30 text-dark-muted cursor-not-allowed'
              }`}
            >
              {isExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              .eml
            </button>
            <button
              onClick={onDownloadMailMergePack}
              disabled={isExporting || sendMode !== 'individual'}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-all duration-150 ${
                sendMode === 'individual'
                  ? 'bg-purple-500/10 border border-purple-500/30 text-purple-400'
                  : 'bg-dark-surface/30 border border-dark-border/30/30 text-dark-muted cursor-not-allowed'
              }`}
            >
              {isExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
              .zip
            </button>
          </div>

          {/* Mark as Completed - Only show if invite has a name */}
          {hasInviteName && onMarkAsCompleted && (
            <div className="flex items-center gap-2 pt-2 border-t border-dark-border/30 mt-2">
              <span className="text-xs text-dark-muted whitespace-nowrap w-20">Complete:</span>
              {isCompleted ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded text-xs">
                  <CheckCircle2 className="w-3 h-3" />
                  Completed
                </span>
              ) : (
                <button
                  onClick={onMarkAsCompleted}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 rounded text-xs hover:bg-green-500/20 transition-all duration-150"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Mark as Completed
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="px-3 py-2 border-t border-dark-border/30 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-3 py-2 text-dark-muted hover:text-white text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex items-center gap-1 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile Message Edit Modal */}
        {showMessageEditModal && (
          <div className="fixed inset-0 z-[500] flex flex-col bg-dark-surface">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border/30">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-white">Edit Personal Message</span>
              </div>
              <button
                onClick={handleCancelMessageEdit}
                className="p-2 text-dark-muted hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="space-y-3">
                {/* Info about merge tokens */}
                <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <span className="text-xs text-amber-300">
                    Use <code className="px-1 py-0.5 bg-dark-surface rounded text-amber-400">{'{{firstName}}'}</code> to personalize.
                  </span>
                </div>

                {/* Textarea */}
                <textarea
                  value={tempMessageOverride}
                  onChange={(e) => setTempMessageOverride(e.target.value)}
                  placeholder="Hi {{firstName}}, I wanted to personally reach out..."
                  className="w-full p-3 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-amber-500 resize-none"
                  style={{ minHeight: '150px' }}
                  autoFocus
                />

                {/* Insert firstName button */}
                <button
                  onClick={handleInsertFirstNameInModal}
                  className="flex items-center gap-1 px-3 py-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg"
                >
                  <Plus className="w-3 h-3" />
                  Insert {'{{firstName}}'}
                </button>

                {/* Live Preview */}
                {tempMessageOverride.trim() && (
                  <div className="p-3 bg-dark-bg border border-dark-border/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-3.5 h-3.5 text-dark-muted" />
                      <span className="text-xs text-dark-muted">Preview (sample: Chris)</span>
                    </div>
                    <p className="text-sm text-white/90 whitespace-pre-wrap">
                      {tempMessageOverride.replace(/\{\{firstName\}\}/g, 'Chris')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 border-t border-dark-border/30">
              <button
                onClick={handleApplyMessageChanges}
                className="w-full py-3 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
              >
                Apply Changes
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop UI (original)
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-dark-border/30">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-white font-medium">Choose Send Mode</span>
          <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
            {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Mode selection cards */}
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-3 sm:space-y-4">
          {/* Individual Mode Card */}
          <button
            onClick={() => onSendModeChange('individual')}
            className={`w-full text-left p-3 sm:p-5 rounded-xl border-2 transition-all ${
              sendMode === 'individual'
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-dark-border/30 bg-dark-bg hover:border-dark-border/30'
            }`}
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <div
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${
                  sendMode === 'individual' ? 'bg-purple-500' : 'bg-dark-border'
                }`}
              >
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-white">Individual Emails</h3>
                  {sendMode === 'individual' && (
                    <span className="px-2 py-0.5 bg-purple-500 text-white rounded text-xs">
                      Selected
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs sm:text-sm text-dark-muted">
                  Each recipient receives their own personalized email with their first name.
                </p>
                <ul className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-dark-text">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 shrink-0" />
                    <span>
                      Personalized greeting: "Hi {'{first_name}'},"
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 shrink-0" />
                    <span>Falls back to "Hi there," if no first name</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400 shrink-0" />
                    <span>
                      Send time: {estimatedTimeText} for {recipientCount} emails
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </button>

          {/* Bulk BCC Mode Card */}
          <button
            onClick={() => onSendModeChange('bulk-bcc')}
            className={`w-full text-left p-3 sm:p-5 rounded-xl border-2 transition-all ${
              sendMode === 'bulk-bcc'
                ? 'border-brand-500 bg-blue-500/10'
                : 'border-dark-border/30 bg-dark-bg hover:border-dark-border/30'
            }`}
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <div
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${
                  sendMode === 'bulk-bcc' ? 'bg-blue-500' : 'bg-dark-border'
                }`}
              >
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-white">Bulk BCC</h3>
                  {sendMode === 'bulk-bcc' && (
                    <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">
                      Selected
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs sm:text-sm text-dark-muted">
                  Single email sent to you with all recipients in BCC (hidden from each other).
                </p>
                <ul className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-dark-text">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 shrink-0" />
                    <span>Instant send (one API call)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 shrink-0" />
                    <span>Recipients hidden from each other via BCC</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400 shrink-0" />
                    <span>You receive the email copy in your inbox</span>
                  </li>
                </ul>
              </div>
            </div>
          </button>

          {/* BCC To Email Input (shown when bulk-bcc is selected) */}
          {sendMode === 'bulk-bcc' && (
            <div className="p-3 sm:p-5 bg-dark-bg border border-dark-border/30 rounded-xl">
              <label className="block text-sm font-medium text-white mb-1.5 sm:mb-2">
                Send Email To (your email)
              </label>
              <p className="text-xs text-dark-muted mb-2 sm:mb-3">
                The email will be addressed to this address. All {recipientCount} recipients will be
                in BCC.
              </p>
              <input
                type="email"
                value={bccToEmail}
                onChange={(e) => onBccToEmailChange(e.target.value)}
                placeholder="evan.roland@usi.com"
                className={`w-full p-2.5 sm:p-3 bg-dark-surface border rounded-lg text-sm text-white placeholder:text-dark-muted focus:outline-none transition-all duration-150 ${
                  bccToEmail && !isValidEmail(bccToEmail)
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-dark-border/30 focus:border-brand-500'
                }`}
              />
              {bccToEmail && !isValidEmail(bccToEmail) && (
                <p className="mt-1 text-xs text-red-400">Please enter a valid email address</p>
              )}

              <label className="flex items-center gap-2 mt-2 sm:mt-3 text-xs text-dark-muted cursor-pointer hover:text-dark-text">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-dark-border/30 bg-dark-bg text-blue-500 focus:ring-brand-500 focus:ring-offset-0"
                />
                Save as default for future emails
              </label>
            </div>
          )}

          {/* Test Mode Section */}
          <div className="p-3 sm:p-5 bg-dark-bg border border-dark-border/30 rounded-xl">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <FlaskConical className={`w-4 h-4 ${testModeEnabled ? 'text-amber-400' : 'text-dark-muted'}`} />
                <span className="text-sm font-medium text-white">Test Mode</span>
                {testModeEnabled && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                    Active
                  </span>
                )}
              </div>
              <button
                onClick={() => onTestModeChange(!testModeEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 ${
                  testModeEnabled ? 'bg-amber-500' : 'bg-dark-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                    testModeEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <p className="text-xs text-dark-muted mb-2 sm:mb-3">
              {testModeEnabled
                ? 'Emails will be sent ONLY to the test addresses below instead of actual recipients.'
                : 'Enable to send test emails to custom addresses before sending to actual recipients.'}
            </p>

            {testModeEnabled && (
              <div className="space-y-1.5 sm:space-y-2">
                {testEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleUpdateTestEmail(index, e.target.value)}
                      placeholder={`Test email ${index + 1}`}
                      className={`flex-1 p-2.5 bg-dark-surface border rounded-lg text-sm text-white placeholder:text-dark-muted focus:outline-none transition-all duration-150 ${
                        email && !isValidEmail(email)
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-dark-border/30 focus:border-amber-500'
                      }`}
                    />
                    {testEmails.length > 1 && (
                      <button
                        onClick={() => handleRemoveTestEmail(index)}
                        className="p-2 text-dark-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-150"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                {testEmails.length < 4 && (
                  <button
                    onClick={handleAddTestEmail}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all duration-150"
                  >
                    <Plus className="w-3 h-3" />
                    Add another email (max 4)
                  </button>
                )}

                {validTestEmails.length > 0 && (
                  <p className="text-xs text-amber-400 mt-2">
                    {validTestEmails.length} valid test email{validTestEmails.length !== 1 ? 's' : ''} will receive the email
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Personal Message Override Section */}
          <div className={`p-3 sm:p-5 bg-dark-bg border rounded-xl transition-all duration-150 ${
            usePersonalMessageOverride && sendMode === 'individual'
              ? 'border-amber-500/50'
              : 'border-dark-border/30'
          }`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className={`w-4 h-4 ${usePersonalMessageOverride ? 'text-amber-400' : 'text-dark-muted'}`} />
                <span className="text-sm font-medium text-white">Personal Message Override</span>
                {usePersonalMessageOverride && sendMode === 'individual' && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                    Active
                  </span>
                )}
              </div>
              <button
                onClick={() => onUsePersonalMessageOverrideChange(!usePersonalMessageOverride)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-150 ${
                  usePersonalMessageOverride ? 'bg-amber-500' : 'bg-dark-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-dark-surface transition-transform ${
                    usePersonalMessageOverride ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <p className="text-xs text-dark-muted mb-2 sm:mb-3">
              {usePersonalMessageOverride
                ? 'Add a personalized message for each recipient that will be included in individual emails.'
                : 'Enable to add a per-recipient personal message for individual sends and mail-merge exports.'}
            </p>

            {usePersonalMessageOverride && (
              <>
                {sendMode === 'bulk-bcc' ? (
                  <div className="p-3 bg-dark-surface/50 border border-dark-border/30 rounded-xl">
                    <p className="text-xs text-dark-muted italic">
                      Personal message override is only available for Individual send mode.
                      Switch to Individual emails above to use this feature.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Info about merge tokens */}
                    <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <span className="text-xs text-amber-300">
                        Use <code className="px-1 py-0.5 bg-dark-surface rounded text-amber-400">{'{{firstName}}'}</code> to personalize with the recipient's first name.
                      </span>
                    </div>

                    {/* Textarea with insert button */}
                    <div className="relative">
                      <textarea
                        ref={textareaRef}
                        value={personalMessageOverride}
                        onChange={(e) => onPersonalMessageOverrideChange(e.target.value)}
                        placeholder="Hi {{firstName}}, I wanted to personally reach out about this event..."
                        className="w-full p-3 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-amber-500 transition-all duration-150 resize-none"
                        style={{ minHeight: '100px' }}
                      />
                      <button
                        onClick={handleInsertFirstName}
                        className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 text-xs text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded transition-all duration-150"
                      >
                        <Plus className="w-3 h-3" />
                        {'{{firstName}}'}
                      </button>
                    </div>

                    {/* Live Preview */}
                    {personalMessageOverride.trim() && (
                      <div className="p-3 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="w-3.5 h-3.5 text-dark-muted" />
                          <span className="text-xs text-dark-muted">Preview with sample data: Chris</span>
                        </div>
                        <p className="text-sm text-white/90 whitespace-pre-wrap">
                          {getPersonalMessagePreview()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Export Options Section */}
          <div className="p-3 sm:p-5 bg-gradient-to-br from-gray-800/50 to-slate-800/30 border border-dark-border/30/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Download className="w-4 h-4 text-dark-muted" />
              <span className="text-sm font-medium text-white">Export Options</span>
              <span className="px-2 py-0.5 bg-dark-surface/50/30 text-dark-muted rounded text-xs">
                Optional
              </span>
            </div>

            <p className="text-xs text-dark-muted mb-3 sm:mb-4">
              Download for manual sending instead of Resend:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Outlook Draft Button */}
              <div className="relative">
                <button
                  onClick={onDownloadOutlookDraft}
                  disabled={isExporting || sendMode !== 'bulk-bcc' || !isValidEmail(bccToEmail)}
                  className={`w-full flex flex-col items-center gap-2 p-4 rounded-lg border transition-all ${
                    sendMode === 'bulk-bcc' && isValidEmail(bccToEmail)
                      ? 'bg-blue-500/10 border-brand-500/30 hover:border-brand-500/50 hover:bg-brand-500/15 cursor-pointer'
                      : 'bg-dark-surface/30 border-dark-border/30/30 cursor-not-allowed opacity-50'
                  }`}
                >
                  {isExporting ? (
                    <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                  ) : (
                    <Mail className="w-6 h-6 text-blue-400" />
                  )}
                  <div className="text-center">
                    <div className="text-sm font-medium text-white">Outlook Draft</div>
                    <div className="text-xs text-dark-muted">(Bulk BCC)</div>
                  </div>
                  <div className="text-xs text-dark-muted">Download .eml</div>
                </button>
                {(sendMode !== 'bulk-bcc' || !isValidEmail(bccToEmail)) && (
                  <div className="mt-1.5 text-xs text-dark-muted text-center">
                    {sendMode !== 'bulk-bcc'
                      ? 'Select Bulk BCC mode'
                      : 'Enter your email above'}
                  </div>
                )}
              </div>

              {/* Outlook Draft Generator Button */}
              <div className="relative">
                <button
                  onClick={onDownloadMailMergePack}
                  disabled={isExporting || sendMode !== 'individual'}
                  className={`w-full flex flex-col items-center gap-2 p-4 rounded-lg border transition-all ${
                    sendMode === 'individual'
                      ? 'bg-purple-500/10 border-purple-500/30 hover:border-purple-500/50 hover:bg-purple-500/15 cursor-pointer'
                      : 'bg-dark-surface/30 border-dark-border/30/30 cursor-not-allowed opacity-50'
                  }`}
                >
                  {isExporting ? (
                    <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                  ) : (
                    <FileText className="w-6 h-6 text-purple-400" />
                  )}
                  <div className="text-center">
                    <div className="text-sm font-medium text-white">Outlook Draft Generator</div>
                    <div className="text-xs text-dark-muted">(Excel + VBA Macro)</div>
                  </div>
                  <div className="text-xs text-dark-muted">Download .zip</div>
                </button>
                {sendMode !== 'individual' && (
                  <div className="mt-1.5 text-xs text-dark-muted text-center">
                    Select Individual mode
                  </div>
                )}
              </div>
            </div>

            <p className="mt-3 sm:mt-4 text-xs text-dark-muted text-center">
              These exports allow you to send emails manually via Outlook instead of using the automated Resend service.</p>
          </div>

          {/* Mark as Completed Section - Only show if invite has a name */}
          {hasInviteName && onMarkAsCompleted && (
            <div className="p-3 sm:p-5 bg-gradient-to-br from-green-900/20 to-emerald-900/10 border border-green-700/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-white">Invite Status</span>
              </div>

              {isCompleted ? (
                <div className="flex items-center gap-3 p-3 bg-green-500/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <div>
                    <div className="text-sm font-medium text-green-400">Invite Completed</div>
                    <p className="text-xs text-green-300/70">This invite has been marked as delivered</p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-dark-muted mb-3">
                    Mark this invite as completed after you've sent emails to all recipients. This will record the delivery history.
                  </p>
                  <button
                    onClick={onMarkAsCompleted}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-all duration-150"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Mark as Completed
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer with navigation */}
      <div className="p-3 sm:p-4 border-t border-dark-border/30 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-dark-muted hover:text-white transition-all duration-150 text-sm order-2 sm:order-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        <button
          onClick={handleNext}
          disabled={!canProceed}
          className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
        >
          Next: Review
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Step3SendMode;
